import { CircularProgress, Collapse, Grid, Typography } from '@mui/material'
import { AxiosError } from 'axios'
import { FastField, Formik, FormikValues } from 'formik'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { FormikVinciInput, VinciButton, VinciInput } from '~/components'
import { IIdentity, NoReturn } from '~/utils/types'
import {
  createIdentity,
  ICreateIdentity,
  IUpdateIdentity,
  searchIdentity,
  updateIdentity,
} from '../../data/userApi'
import { identityValidationSchema } from '../../utils/validationSchema'

interface IIdentitySelectorProps {
  currentIdentity: IIdentity | null
  onChangeIdentity: (newIdentityId: IIdentity, isNew?: boolean, newPassword?: string) => NoReturn
  onTouch?: () => NoReturn
}

type TSelectionState = 'selecting' | 'showing' | 'editing'
export const IdentitySelector: FC<IIdentitySelectorProps> = ({
  currentIdentity,
  onChangeIdentity,
  onTouch,
}) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()
  const [state, setState] = useState<TSelectionState>(
    currentIdentity === null ? 'selecting' : 'showing',
  )
  const [searchEmail, setSearchEmail] = useState(currentIdentity?.email ?? '')

  const searchMutation = useMutation({
    mutationFn: searchIdentity,
    onSuccess: (foundIdentity: IIdentity) => {
      onChangeIdentity(foundIdentity, false)
      setState('showing')
    },
  })

  const createMutation = useMutation({
    mutationFn: createIdentity,
    onError: (error) => {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.message ??
            'There was an error creating identity, please try again.',
        )
        return
      }
      toast.error('There was an error creating identity, please try again.')
    },
    onSuccess: (createdIdentity: IIdentity, values) => {
      searchMutation.reset()
      onChangeIdentity(createdIdentity, true, values.password)
      setState('showing')
    },
  })

  const updateMutation = useMutation({
    mutationFn: updateIdentity,
    onMutate: () => {
      const toastId = toast.loading(t('Updating identity...'))
      return toastId
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.message ??
            'There was an error updating identity, please try again.',
        )
        return
      }
      toast.error('There was an error updating identity, please try again.')
    },
    onSuccess: (createdIdentity: IIdentity, _, context) => {
      if (context) {
        toast.update(context, {
          render: t('Identity updated successfully'),
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        })
      }
      searchMutation.reset()
      onChangeIdentity(createdIdentity, false)
      queryClient.invalidateQueries(['list_all_users'])
      setState('showing')
    },
  })

  const handleCreateIdentity = useCallback(
    async (values: FormikValues) => {
      const identity = values as ICreateIdentity
      if (identity.password === '') {
        toast.error(t('Password is required.'))
        return
      }
      createMutation.mutate(identity)
    },
    [createMutation, t],
  )

  const handleUpdateIdentity = useCallback(
    async (values: FormikValues) => {
      const updateIdentityPayload = {
        identity_id: currentIdentity?._id ?? '',
        ...values,
      } as IUpdateIdentity

      if (updateIdentityPayload.password === '') {
        delete updateIdentityPayload.password
      }
      updateMutation.mutate(updateIdentityPayload)
    },
    [currentIdentity, updateMutation],
  )

  const isIdentityDefined = useMemo(() => currentIdentity !== null, [currentIdentity])

  const initialIdentity = {
    name: currentIdentity?.name ?? '',
    email: searchEmail,
    password: '',
  } as IIdentity

  return (
    <>
      <Collapse in={state === 'selecting' && !searchMutation.isError}>
        <Grid container mt={4}>
          <Grid item xs={8}>
            <VinciInput
              disabled={searchMutation.isLoading}
              fullWidth
              label='Email'
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  searchMutation.mutate(searchEmail)
                }
              }}
              onBlur={onTouch}
            />
          </Grid>
          <Grid item xs={4} display='flex' justifyContent='flex-end' alignItems='center'>
            <VinciButton
              variant='outlined'
              onClick={() => searchMutation.mutate(searchEmail)}
              disabled={searchMutation.isLoading || searchEmail.trim() === ''}
              sx={{ height: '100%' }}
              startIcon={
                <Collapse
                  component='div'
                  in={searchMutation.isLoading}
                  orientation='horizontal'
                  sx={{
                    maxHeight: 18.8,
                  }}
                >
                  <CircularProgress size={16} sx={{ mb: 1 }} />
                </Collapse>
              }
            >
              {t('Check')}
            </VinciButton>
          </Grid>
        </Grid>
      </Collapse>
      <Collapse in={searchMutation.isError || state === 'editing'}>
        <Formik
          initialValues={initialIdentity}
          enableReinitialize
          validationSchema={identityValidationSchema}
          onSubmit={(values, { setSubmitting }) => {
            if (isIdentityDefined) {
              setSubmitting(false)
              handleUpdateIdentity(values)
            } else {
              handleCreateIdentity(values)
            }
          }}
          isInitialValid={false}
          validateOnBlur
        >
          {(form) => (
            <Grid container mt={4} gap={4}>
              <Grid item xs={12}>
                <FastField
                  component={FormikVinciInput}
                  name='name'
                  label={t('Name')}
                  fullWidth
                  error={Boolean(form.errors.name)}
                  helperText={form.errors.name}
                  onBlur={form.handleBlur}
                />
              </Grid>
              <Grid item xs={12}>
                <FastField
                  component={FormikVinciInput}
                  name='email'
                  label={t('Email')}
                  fullWidth
                  error={Boolean(form.errors.password)}
                  helperText={form.errors.password}
                  onBlur={form.handleBlur}
                />
              </Grid>
              <Grid item xs={12}>
                <FastField
                  component={FormikVinciInput}
                  name='password'
                  label={t('Password')}
                  fullWidth
                  autoComplete='off'
                  type='password'
                  error={Boolean(form.errors.password)}
                  helperText={form.errors.password}
                  onBlur={form.handleBlur}
                />
              </Grid>
              <Grid item xs={12} display='flex' justifyContent='flex-end' gap={3}>
                <VinciButton
                  variant='outlined'
                  onClick={() => {
                    setState(isIdentityDefined ? 'showing' : 'selecting')
                    searchMutation.reset()
                  }}
                >
                  Back
                </VinciButton>
                <VinciButton
                  // type='submit'
                  onClick={(e) => {
                    e.stopPropagation()
                    form.submitForm()
                  }}
                  disabled={!form.isValid || createMutation.isLoading}
                  startIcon={
                    <Collapse
                      component='div'
                      in={createMutation.isLoading}
                      orientation='horizontal'
                      sx={{
                        maxHeight: 18.8,
                      }}
                    >
                      <CircularProgress size={16} sx={{ mb: 1, color: '#fff' }} />
                    </Collapse>
                  }
                >
                  {isIdentityDefined ? 'Update' : 'Create'}
                </VinciButton>
              </Grid>
            </Grid>
          )}
        </Formik>
      </Collapse>
      <Collapse in={state === 'showing'}>
        <Grid container mt={4}>
          <Grid item xs={6}>
            {isIdentityDefined ? (
              <>
                <Typography variant='subtitle1'>{currentIdentity?.name}</Typography>
                <Typography variant='caption'>{currentIdentity?.email}</Typography>
              </>
            ) : (
              <Typography variant='subtitle1'>{t('Identity not set.')}</Typography>
            )}
          </Grid>
          <Grid item xs={6} display='flex' justifyContent='flex-end' alignItems='center'>
            <VinciButton
              onClick={() => {
                searchMutation.reset()
                if (isIdentityDefined) {
                  setState('editing')
                } else {
                  setState('selecting')
                }
              }}
            >
              {t(isIdentityDefined ? 'Change' : 'Define Email')}
            </VinciButton>
          </Grid>
        </Grid>
      </Collapse>
    </>
  )
}
